import React, { useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faExclamation,
  faCheck,
  faUpRightAndDownLeftFromCenter,
  faDownLeftAndUpRightToCenter,
  faUpload,
  faDownload,
  faCopy,
  faPaste,
  faClockRotateLeft,
} from '@fortawesome/free-solid-svg-icons'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/mode-html'
import 'ace-builds/src-noconflict/mode-sparql'
import 'ace-builds/src-noconflict/theme-ambiance'
import { Tooltip } from '@mui/material'
import clsx from 'clsx'
import style from './JsonEditor.module.css'
import { exportAsFile } from '../utils/export-as-file'
import { countLines, formatAge, formatDifference, formatSize, previewOf, useLocalStorageHistory } from '../hooks/use-local-storage-history'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui-primitives/dropdown-menu'

/**
 * The editor shell behind the code-valued config fields: an Ace editor at a
 * fixed height with an expand toggle, and a toolbar for save, upload, copy and
 * paste.
 *
 * Language-specific behaviour is passed in rather than branched on here.
 * `validate` and `format` are optional, and their buttons are absent when they
 * are: a JSON field can be checked and pretty-printed, an HTML page cannot be
 * usefully either, and a shell that pretended otherwise would show buttons that
 * do nothing.
 *
 * It borrows JsonEditor.module.css rather than owning a stylesheet, because the
 * two are the same chrome and duplicating it would let them drift.
 */
export type CodeTextEditorProps = {
  name?: string
  exportFileName?: string
  value: string
  onValueChange?: (value: string) => void
  readonly?: boolean
  /** An Ace mode whose `ace-builds/src-noconflict/mode-<mode>` module is imported above. */
  mode: 'json' | 'html' | 'sparql'
  /** Offered when saving, and accepted when uploading. */
  fileExtension: string
  mimeType: string
  /** Height when collapsed; the expand toggle goes to 80vh either way. */
  collapsedHeight?: string
  /** Syntax check behind the "check" button. Returns 'ok' or the message to show. */
  validate?: (value: string) => 'ok' | string
  /** Reformats the value behind its own button. */
  format?: (value: string) => string
  formatLabel?: React.ReactNode
  /** Gutter icons with a tooltip, one per row. */
  annotations?: CodeAnnotation[]
  /** Row highlights behind the text. Needs a className with a real stylesheet rule — Ace renders these outside any CSS module. */
  markers?: CodeMarker[]
  /** The Ace editor, once it exists, for callers that need to scroll or select. */
  onEditorLoad?: (editor: AceEditorInstance) => void
  /** Row clicked in the gutter, zero-based, as Ace reports it. */
  onGutterClick?: (row: number) => void
  /**
   * Identifies this field's version history in localStorage. Absent means no
   * history is kept and no history button renders.
   */
  localHistoryKey?: string
}

export type CodeAnnotation = {
  /** Zero-based, unlike the line numbers shown in the gutter. */
  row: number
  column: number
  text: string
  type: 'error' | 'warning' | 'info'
}

export type CodeMarker = {
  startRow: number
  startCol: number
  endRow: number
  endCol: number
  className: string
  type: 'fullLine'
}

/** Ace's editor object. Loosely typed on purpose: brace ships no types and react-ace's cover only part of it. */
export type AceEditorInstance = any

export function CodeTextEditor(props: CodeTextEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [validationStatus, setValidationStatus] = useState<string | 'ok' | null>(null)

  const { validate, format } = props
  const editable = !props.readonly
  const history = useLocalStorageHistory(props.localHistoryKey, props.value, (restored) =>
    props.onValueChange?.(restored),
  )

  /*
   * Described once per change to the list rather than per keystroke: the editor
   * re-renders on every character, and a preview scans the whole page.
   */
  const described = useMemo(
    () =>
      history.versions.map((version) => ({
        version,
        preview: previewOf(version.value),
        chars: version.value.length,
        lines: countLines(version.value),
      })),
    [history.versions],
  )
  const currentLines = useMemo(
    () => (props.localHistoryKey ? countLines(props.value) : 0),
    [props.localHistoryKey, props.value],
  )

  /* Ace keeps the listener registered in onLoad; reading the callback through a ref keeps it current. */
  const onGutterClick = useRef(props.onGutterClick)
  onGutterClick.current = props.onGutterClick

  const check = () => {
    if (!validate) return
    setValidationStatus(validate(props.value))
  }

  const reformat = () => {
    if (!format) return
    props.onValueChange?.(format(props.value))
  }

  const save = () => {
    const filename = props.exportFileName || `${props.name || 'data'}.${props.fileExtension}`
    exportAsFile(props.value, filename, { type: props.mimeType })
  }

  const upload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result
      if (text) props.onValueChange?.(text as string)
    }
    reader.readAsText(file)
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(props.value)
    } catch (err) {
      console.error(err)
      alert('Failed to copy')
    }
  }

  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      props.onValueChange?.(text)
    } catch (err) {
      console.error(err)
      alert('Failed to paste')
    }
  }

  return (
    <div className={style.wrapper}>
      <AceEditor
        key={String(expanded)}
        name={props.name}
        mode={props.mode}
        theme="ambiance"
        fontSize="12pt"
        style={{
          width: '100%',
          height: expanded ? '80vh' : (props.collapsedHeight ?? '120px'),
          fontFamily: 'Consolas, Inconsolata, Monaco, "Lucida Console", monospace',
          fontWeight: 500,
        }}
        value={props.value}
        readOnly={props.readonly}
        /*
         * No Ace syntax worker: ace-builds loads it as a separate file the bundle does not serve, and its
         * annotations would replace the ones passed in above.
         */
        setOptions={{ useWorker: false }}
        annotations={props.annotations}
        /* Cast: react-ace types markers with its own MarkerType union, which 'fullLine' satisfies but not by name. */
        markers={props.markers as any}
        onLoad={(editor: AceEditorInstance) => {
          props.onEditorLoad?.(editor)
          editor.on('guttermousedown', (event: any) => {
            onGutterClick.current?.(event.getDocumentPosition().row)
          })
        }}
        onChange={(value) => {
          if (props.readonly) return
          setValidationStatus(null)
          props.onValueChange?.(value)
        }}
      />
      <div className={style.bar}>
        {editable && validate && (
          <Tooltip title="Check syntax">
            <button className="px-1.5 py-px" type="button" onClick={check}>
              check
            </button>
          </Tooltip>
        )}
        {validationStatus && (
          <div className={clsx(style['json-status'], validationStatus !== 'ok' ? style.error : style.ok)}>
            <FontAwesomeIcon icon={validationStatus === 'ok' ? faCheck : faExclamation} />
            {validationStatus === 'ok' ? 'Valid' : String(validationStatus)}
          </div>
        )}
        <div className={style.separator} />
        {editable && format && (
          <Tooltip title="Format">
            <button className="px-1.5 py-px" type="button" onClick={reformat}>
              {props.formatLabel ?? <b>{`{ }`}</b>}
            </button>
          </Tooltip>
        )}
        {editable && props.localHistoryKey && (
          <DropdownMenu>
            <Tooltip title="Earlier versions of this field">
              <DropdownMenuTrigger asChild>
                <button className="px-1.5 py-px" type="button">
                  <FontAwesomeIcon icon={faClockRotateLeft} />
                </button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent align="end" className="max-w-[48ch]">
              {/*
                * The button shows whenever the field keeps history, empty or not.
                * Hiding it until the first version exists made a working feature
                * look like a broken one, and left the thing that produces a
                * version - pausing after an edit - undiscoverable.
                */}
              {described.length === 0 && (
                <DropdownMenuLabel className="font-normal text-xs opacity-60">
                  No earlier versions yet. One is kept whenever you pause after editing, in this
                  browser only.
                </DropdownMenuLabel>
              )}
              {described.map(({ version, preview, chars, lines }) => {
                const difference = formatDifference(
                  { lines, chars },
                  { lines: currentLines, chars: props.value.length },
                )
                return (
                  <DropdownMenuItem
                    key={version.at}
                    className="flex-col items-start gap-0.5 cursor-pointer"
                    onSelect={() => history.restore(version)}>
                    <span className="text-xs opacity-70">
                      {formatAge(version.at)} · {formatSize(chars)}
                      {difference && ` · ${difference}`}
                    </span>
                    <span className="w-full truncate font-mono text-xs">{preview}</span>
                  </DropdownMenuItem>
                )
              })}
              {described.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onSelect={() => history.clear()}>
                    Clear history
                  </DropdownMenuItem>
                  <DropdownMenuLabel className="font-normal text-xs opacity-60">
                    Kept in this browser only
                  </DropdownMenuLabel>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Tooltip title={`Save as .${props.fileExtension}`}>
          <button className="px-1.5 py-px" type="button" onClick={save}>
            <FontAwesomeIcon icon={faDownload} />
          </button>
        </Tooltip>
        {editable && (
          <Tooltip title={`Upload .${props.fileExtension}`}>
            <button className="px-1.5 py-px" type="button" onClick={() => inputRef.current?.click?.()}>
              <input
                ref={inputRef}
                type="file"
                accept={`.${props.fileExtension}`}
                style={{ display: 'none' }}
                onChange={upload}
              />
              <FontAwesomeIcon icon={faUpload} />
            </button>
          </Tooltip>
        )}
        <Tooltip title="Copy to clipboard">
          <button className="px-1.5 py-px" type="button" onClick={copy}>
            <FontAwesomeIcon icon={faCopy} />
          </button>
        </Tooltip>
        {editable && (
          <Tooltip title="Paste from clipboard">
            <button className="px-1.5 py-px" type="button" onClick={paste}>
              <FontAwesomeIcon icon={faPaste} />
            </button>
          </Tooltip>
        )}
        <Tooltip
          title={expanded ? 'Collapse' : 'Expand'}
          onClick={() => setExpanded((prevExpanded) => !prevExpanded)}>
          <button className="px-1.5 py-px" type="button">
            <FontAwesomeIcon icon={expanded ? faDownLeftAndUpRightToCenter : faUpRightAndDownLeftFromCenter} />
          </button>
        </Tooltip>
      </div>
    </div>
  )
}
